import React, { useState, useEffect } from 'react';
import { FaMoon } from 'react-icons/fa';

export function QuietHoursModal({ isOpen, onClose, quietHours, onSave }) {
  const [enabled, setEnabled] = useState(quietHours?.enabled || false);
  const [startTime, setStartTime] = useState(quietHours?.start_time || '22:00');
  const [endTime, setEndTime] = useState(quietHours?.end_time || '07:00');
  const [allowEmergency, setAllowEmergency] = useState(quietHours?.allow_emergency !== false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (quietHours) {
      setEnabled(quietHours.enabled || false);
      setStartTime(quietHours.start_time || '22:00');
      setEndTime(quietHours.end_time || '07:00');
      setAllowEmergency(quietHours.allow_emergency !== false);
    }
  }, [quietHours]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        enabled,
        start_time: startTime,
        end_time: endTime,
        allow_emergency: allowEmergency
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="quiet-hours-modal">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
            <FaMoon className="text-indigo-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-800">Quiet Hours</h3>
        </div>
        
        <p className="text-gray-600 mb-6">
          During quiet hours, you won't receive notification sounds. Messages will still arrive.
        </p>
        
        {/* Enable Toggle */}
        <div className="flex items-center justify-between mb-6 p-3 bg-gray-50 rounded-lg">
          <span className="font-medium text-gray-800">Enable Quiet Hours</span>
          <button
            onClick={() => setEnabled(!enabled)}
            className={`w-12 h-6 rounded-full transition-colors ${
              enabled ? 'bg-blue-600' : 'bg-gray-300'
            }`}
            data-testid="quiet-hours-toggle"
          >
            <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-0.5'
            }`} />
          </button>
        </div>
        
        {enabled && (
          <div className="space-y-4">
            {/* Time Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="quiet-hours-start"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="quiet-hours-end"
                />
              </div>
            </div>
            
            {/* Allow Emergency */}
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <div>
                <span className="font-medium text-gray-800">Allow Emergency Messages</span>
                <p className="text-xs text-gray-500">Still notify for urgent alerts</p>
              </div>
              <button
                onClick={() => setAllowEmergency(!allowEmergency)}
                className={`w-12 h-6 rounded-full transition-colors ${
                  allowEmergency ? 'bg-red-500' : 'bg-gray-300'
                }`}
                data-testid="allow-emergency-toggle"
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
                  allowEmergency ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
          </div>
        )}
        
        {/* Actions */}
        <div className="flex space-x-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            data-testid="save-quiet-hours"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
